import React from 'react';
import noop from 'utils/noop';
import { SemanticUI } from '@iterable/deprecated_react_components';
import { BRAND_AFFINITY } from 'utils/fieldConstants';
import { DISPLAY_TYPES } from './constants';
import { DropdownIcon } from './DropdownIcon';
import { ReadOnlyLabel, StyledCoreDropdown } from './FieldDropdown.styled';
import { Field } from '../fieldTypeaheadSelect/types';

type Props = {
  /** Additional classes. */
  className: string;
  /** Additional classes for readOnly component. */
  classNameReadOnly: string;
  /** If dropdown is clearable. */
  clearable?: boolean;
  /** If dropdown has error. */
  error: boolean;
  /** Available fields for the selected property. */
  fields: Field[];
  /** Hides the read only label. */
  hideReadOnlyLabel?: boolean;
  /** If query builder is in read only mode. */
  isReadOnly?: boolean;
  /** Callback function when value changes. */
  onChange: (
    e: React.SyntheticEvent<HTMLElement, Event>,
    data: { type: string; value: string },
  ) => void;
  /** Placeholder text of the dropdown. */
  placeholder: string;
  /** The type of the selected field. */
  type: string;
  /** Open dropdown menu upwards. */
  upward: boolean;
  /** Value of the selected field. */
  value: string;
  /** Flag if fieldtypes are loading */
  isLoading: boolean;
  /** Data test */
  dataTest: string;
};

const VALUE_DELIMITER = ',';

export default class FieldDropdown extends React.Component<Props> {
  static defaultProps = {
    fields: [],
    onChange: noop,
    placeholder: 'Select a field...',
    readOnly: false,
    upward: false,
    isReadOnly: false,
  };

  /**
   * formatValue
   * Formats the selected value by combining value and type in order to pass type when value changes.
   * @param { String } value: The current value.
   * @param { String } type: The current type.
   * @returns { String } The formatted value.
   */
  formatValue = (value: string, type: string) => {
    if (value && type) {
      return `${value}${VALUE_DELIMITER}${type}`;
    }
    return '';
  };

  /**
   * formatFields
   * Formats the field options to use in the dropdown.
   * @returns { Array } The formatted field options.
   */
  formatFields = () => {
    const { fields, value: selectedValue, isReadOnly } = this.props;
    let label = '';
    let selectedType = '';

    let selectedIndex = null;

    const options = fields.map((field, index) => {
      const { type, text, value, ...otherProps } = field;
      const selectedLabel = text || value;

      const option = {
        ...otherProps,
        // in case display value of type needs to be changed
        description: DISPLAY_TYPES[type] || type,
        text: selectedLabel,
        value: this.formatValue(value, type),
        icon: value === BRAND_AFFINITY ? <DropdownIcon value={value} /> : null,
        disabled: isReadOnly,
      };

      if (value === selectedValue) {
        label = selectedLabel;
        selectedType = type;
        selectedIndex = index;
      }

      return option;
    });
    return { label, options, selectedType, selectedIndex };
  };

  /**
   * handleChange
   * Callback function when the value changes.
   * @param { Object } e: The change event.
   * @param { Object } props: The updated props containing the new value.
   * @returns { void }
   */
  handleChange = (
    e: React.SyntheticEvent<HTMLElement, Event>,
    { value }: SemanticUI.DropdownProps | SemanticUI.DropdownItemProps,
  ): void => {
    const { onChange } = this.props;
    const values = (value as string).split(VALUE_DELIMITER);
    onChange(e, { ...this.props, value: values[0], type: values[1] });
  };

  render() {
    const {
      className,
      classNameReadOnly,
      clearable,
      error,
      hideReadOnlyLabel,
      placeholder,
      type,
      upward,
      value,
      isLoading,
      dataTest,
      isReadOnly,
    } = this.props;
    const { label, options, selectedType, selectedIndex } = this.formatFields();
    return (
      <>
        {!hideReadOnlyLabel && <ReadOnlyLabel className={classNameReadOnly}>{label}</ReadOnlyLabel>}
        <StyledCoreDropdown
          searchInput={{ autoComplete: 'chrome-off' }}
          autoComplete={'off'}
          clearable={clearable}
          className={className}
          isError={error}
          isLoading={isLoading}
          disabled={isReadOnly}
          search
          onChange={(event: any, data: SemanticUI.DropdownProps | SemanticUI.DropdownItemProps) =>
            this.handleChange(event, data)
          }
          options={options}
          placeholder={placeholder}
          upward={upward}
          value={this.formatValue(value, type || selectedType)}
          selectedIndex={selectedIndex}
          data-test={dataTest}
          data-readonly={isReadOnly}
          useVirtualized={options.length > 10}
        />
      </>
    );
  }
}
